import { Outlet, useLocation } from 'react-router-dom'
import {ContentArea} from '@/components/shared/ContentArea'
import {InlineRow} from '@/components/shared/InlineRow'
import { Shield } from 'lucide-react'
import { CountBadge } from '@/components/shared/CountBadge'
import { useAuth } from '@/contexts/auth-context'

import { usePageTitle } from '@/hooks/use-page-title'
import { useI18n } from '@/i18n'
import { SidebarNav, type SidebarNavItem } from '@/components/layout/SidebarNav'
import { SidebarNavId } from '@/testids/SidebarNavTestIds'
import { routes } from '@/lib/routes'
import { permissions } from '@/lib/permissions'
import { CONCEPT_ICON } from '@/lib/concepts'
import { useErrorLogBadgeCount } from '@/hooks/use-error-logs'
import { useAppConfig } from '@/hooks/use-app-config'

import {EmptyState} from '@/components/shared/EmptyState'
import {SectionContainer} from '@/components/shared/SectionContainer'

interface AdminMenuItem extends SidebarNavItem {
  permission: string
  devOnly?: boolean
}

export function Admin() {
  const i18n = useI18n()
  usePageTitle(i18n.admin.pageTitle)
  const location = useLocation()
  const { hasPermission } = useAuth()
  const { devMode } = useAppConfig()
  const isRootAdmin = location.pathname === routes.admin.root()
  const { data: badgeData } = useErrorLogBadgeCount()
  const failureBadgeCount = badgeData?.patternCount ?? 0

  const sectionIdpulseAccess = String(i18n.admin.sectionIdpulseAccess)
  const sectionActivity = String(i18n.admin.sectionActivity)
  const sectionDiagnostics = String(i18n.admin.sectionDiagnostics)
  const sectionConfigurationTooling = String(i18n.admin.sectionConfigurationTooling)

  const menuItems: AdminMenuItem[] = [
    {
      id: SidebarNavId.Roles,
      href: routes.admin.roles.list(),
      icon: CONCEPT_ICON.adminRole,
      permission: permissions.roles.view,
      title: i18n.admin.adminRoles,
      description: i18n.admin.adminRolesDescription,
      section: sectionIdpulseAccess,
    },
    {
      id: SidebarNavId.RoleAssignments,
      href: routes.admin.roleAssignments.path(),
      icon: CONCEPT_ICON.roleAssignment,
      permission: permissions.roleAssignments.view,
      title: i18n.admin.roleAssignments,
      description: i18n.admin.roleAssignmentsDescription,
      section: sectionIdpulseAccess,
    },
    {
      id: SidebarNavId.ServiceAccounts,
      href: routes.admin.serviceAccounts.list(),
      icon: CONCEPT_ICON.serviceAccount,
      permission: permissions.serviceAccounts.view,
      title: i18n.admin.serviceAccounts,
      description: i18n.admin.serviceAccountsDescription,
      section: sectionIdpulseAccess,
    },
    {
      id: SidebarNavId.LifecycleEvents,
      href: routes.admin.lifecycleEvents.path(),
      icon: CONCEPT_ICON.lifecycleEvents,
      permission: permissions.analytics.view,
      title: i18n.lifecycleEvents.dashboard.title,
      description: i18n.lifecycleEvents.dashboard.menuDescription,
      section: sectionActivity,
    },
    {
      id: SidebarNavId.NotificationLog,
      href: routes.admin.notificationLog.path(),
      icon: CONCEPT_ICON.notification,
      permission: permissions.notifications.admin,
      title: i18n.admin.notificationLog,
      description: i18n.admin.notificationLogDescription,
      section: sectionActivity,
    },
    {
      id: SidebarNavId.RequestInspector,
      href: routes.admin.requestInspector.list(),
      icon: CONCEPT_ICON.requestInspector,
      permission: permissions.observability.view,
      title: i18n.admin.requestInspector,
      description: i18n.admin.requestInspectorDescription,
      section: sectionDiagnostics,
    },
    {
      id: SidebarNavId.ErrorLogs,
      href: routes.admin.errorLogs.list(),
      icon: CONCEPT_ICON.errorLog,
      permission: permissions.errorLogs.view,
      title: i18n.admin.errorLogs,
      description: i18n.admin.errorLogsDescription,
      badge: <CountBadge count={failureBadgeCount} variant="destructive" data-testid="failure-badge"/>,
      section: sectionDiagnostics,
    },
    {
      id: SidebarNavId.ScriptExecuteLog,
      href: routes.admin.scriptExecuteLog.list(),
      icon: CONCEPT_ICON.scriptExecuteLog,
      permission: permissions.scriptExecuteLog.view,
      title: i18n.admin.scriptExecuteLog,
      description: i18n.admin.scriptExecuteLogDescription,
      section: sectionDiagnostics,
    },
    {
      id: SidebarNavId.LlmPrompts,
      href: routes.admin.llmPrompts.list(),
      icon: CONCEPT_ICON.llmPrompt,
      permission: permissions.llmPrompts.manage,
      title: i18n.admin.llmPrompts,
      description: i18n.admin.llmPromptsDescription,
      section: sectionConfigurationTooling,
    },
    {
      id: SidebarNavId.Caches,
      href: routes.admin.caches.list(),
      icon: CONCEPT_ICON.cache,
      permission: permissions.caches.view,
      title: i18n.admin.caches,
      description: i18n.admin.cachesDescription,
      section: sectionDiagnostics,
    },
    {
      id: SidebarNavId.NotificationTemplates,
      href: routes.admin.notificationTemplates.list(),
      icon: CONCEPT_ICON.notificationTemplate,
      permission: permissions.notifications.admin,
      title: i18n.admin.notificationTemplates,
      description: i18n.admin.notificationTemplatesDescription,
    },
    {
      id: SidebarNavId.TestData,
      href: routes.admin.testData.path(),
      icon: CONCEPT_ICON.testData,
      permission: permissions.roles.create,
      title: i18n.admin.testData,
      description: i18n.admin.testDataDescription,
      devOnly: true,
      section: sectionConfigurationTooling,
    },
  ]

  const visibleMenuItems = menuItems.filter(item =>
    hasPermission(item.permission) && (!item.devOnly || devMode)
  )

  return (
    <SectionContainer>
      <InlineRow align="start">
        <SidebarNav items={visibleMenuItems} aria-label={String(i18n.admin.pageTitle)}/>

        {/* Content Area */}
        <ContentArea>
          {isRootAdmin ? (
            <EmptyState
              variant="card"
              icon={Shield}
              title={String(i18n.admin.welcome)}
              description={String(i18n.admin.selectOption)}
              data-testid="admin-welcome-card"
            />
          ) : (
            <Outlet />
          )}
        </ContentArea>
      </InlineRow>
    </SectionContainer>
  )
}
